// /login/callback — lands the session after a successful SSO round-trip.
//
// The backend's OIDC/SAML/LDAP callback handlers (api/internal/handlers/
// {oidc,saml,ldap}.go) redirect here with `?token=<jwt>` once they've
// authenticated the user — see the doc comment on OIDCHandlers.CallbackHandler
// for why it's a query param rather than a cookie (CORS runs with
// AllowCredentials: false, so an HttpOnly cookie would be unreadable by the
// SPA's fetch client, which is Bearer-token-only).
//
// SAML's ACS handler additionally round-trips `return_to` — a deep link
// carried through the IdP as RelayState (either forwarded from our own
// /auth/saml/login?return_to=, or supplied unprompted by the IdP itself, as
// Entra's MyApps tile does) — so a login that started (or was targeted) at a
// specific page lands back there instead of always at "/".

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { setToken } from '../lib/auth';
import { buttonClasses, Card } from '../components/ui';

// The backend already validates RelayState/return_to as a same-origin
// relative path before it ever reaches this URL (see saml.go's
// isSafeRelayState), but re-checking client-side costs nothing and means
// this page never trusts a redirect target it didn't itself verify.
function isSafeReturnTo(path: string | null): path is string {
  return !!path && path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\');
}

export function LoginCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // The token arrives in the URL fragment (#token=...), which browsers never
    // send to the server — keeping the JWT out of reverse-proxy access logs.
    // The query string is still read as a fallback so a session started by an
    // older backend build does not break mid-rollout.
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const token = hash.get('token') ?? searchParams.get('token');
    if (!token) {
      setError('No session token was returned by the identity provider.');
      return;
    }
    setToken(token);
    const returnTo = hash.get('return_to') ?? searchParams.get('return_to');
    // replace: true drops the token-bearing entry from browser history.
    navigate(isSafeReturnTo(returnTo) ? returnTo : '/', { replace: true });
  }, [searchParams, navigate]);

  if (!error) return null;

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <Card className="w-full max-w-sm p-8">
        <p className="orb-eyebrow text-danger mb-2">sign-in failed</p>
        <p className="text-sm text-fg mb-6">{error}</p>
        <Link to="/login" className={buttonClasses()}>
          Back to sign in
        </Link>
      </Card>
    </div>
  );
}
